import { Box, Spinner, Text, Tooltip, useTheme } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useCreateHomework from "../../hooks/api/homework/useCreateHomework";
import { Exercise } from "../../models/Exercise";

type Props = {
  exam: Exercise;
};

export default function ExerciseItem({ exam }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const homework = useMemo(() => exam.homeworks?.[0], [exam.homeworks]);
  const { mutateAsync: createHomework, isPending: isCreateHomeworkPending } =
    useCreateHomework(exam.id!);

  const [bg, borderColor, tooltip] = useMemo(() => {
    if (!homework)
      return ["white", "gray.300", t("learning_path_progresses_page.todo")];

    switch (homework.status) {
      case "STARTED":
        return [
          "white",
          theme.colors.primary.DEFAULT,
          t("learning_path_progresses_page.started"),
        ];
      case "DONE":
        return [
          `linear-gradient(white 50%, ${theme.colors.primary[200]} 50% )`,
          theme.colors.primary.DEFAULT,
          t("learning_path_progresses_page.done"),
        ];
      case "REJECTED":
        return [
          theme.colors.red[500],
          theme.colors.red[200],
          t("learning_path_progresses_page.rejected"),
        ];
      case "APPROVED":
        return [
          theme.colors.green[100],
          theme.colors.green[500],
          t("learning_path_progresses_page.approved"),
        ];
      default:
        return ["white", "gray.300", t("learning_path_progresses_page.todo")];
    }
  }, [homework, t, theme.colors.green, theme.colors.primary, theme.colors.red]);

  const onClick = useCallback(() => {
    if (!homework) {
      createHomework().then((createdHomework) =>
        navigate(`./homeworks/${createdHomework?.id}?exerciseId=${exam.id}`),
      );
    } else {
      navigate(`./homeworks/${homework?.id}?exerciseId=${exam.id}`);
    }
  }, [createHomework, exam.id, homework, navigate]);

  if (isCreateHomeworkPending) {
    return <Spinner size="lg" color="primary.DEFAULT" />;
  }

  return (
    <Tooltip label={tooltip}>
      <Box
        cursor={"pointer"}
        aspectRatio={1}
        width={9}
        display="flex"
        justifyContent="center"
        alignItems="center"
        bg={bg}
        borderColor={borderColor}
        borderWidth={2}
        borderRadius={100}
        onClick={onClick}
      >
        <Text as="span" fontSize="md" fontWeight={500}>
          {exam.position + 1}
        </Text>
      </Box>
    </Tooltip>
  );
}
